import { useState, useEffect, useCallback } from 'react';
import { suppliersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialSuppliers } from '../data/suppliers';
import type { Supplier } from '../types';

interface UseSuppliersReturn {
  suppliers: Supplier[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (supplier: Omit<Supplier, 'id'>) => Promise<Supplier>;
  update: (id: number, updates: Partial<Supplier>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export const useSuppliers = (enabled = true): UseSuppliersReturn => {
  const [suppliers, setSuppliers] = useState<Supplier[]>(initialSuppliers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuppliers = useCallback(async () => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setSuppliers(initialSuppliers);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data.length > 0 ? data : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load suppliers';
      setError(errorMessage);
      console.error('Error fetching suppliers:', err);
      setSuppliers(initialSuppliers);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchSuppliers();
      return;
    }
    setLoading(true);
    const unsubscribe = suppliersService.subscribe((updated) => {
      setSuppliers(updated);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [enabled, fetchSuppliers]);

  const create = useCallback(async (supplier: Omit<Supplier, 'id'>): Promise<Supplier> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const newSupplier: Supplier = { ...supplier, id: Date.now() };
      setSuppliers((prev) => [...prev, newSupplier]);
      return newSupplier;
    }
    setError(null);
    const created = await suppliersService.create(supplier);
    setSuppliers((prev) => [...prev.filter((s) => s.id !== created.id), created]);
    return created;
  }, []);

  const update = useCallback(async (id: number, updates: Partial<Supplier>): Promise<void> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setSuppliers((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
      return;
    }
    setError(null);
    await suppliersService.update(id, updates);
    setSuppliers((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      return;
    }
    setError(null);
    await suppliersService.delete(id);
    setSuppliers((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return {
    suppliers,
    loading,
    error,
    refresh: fetchSuppliers,
    create,
    update,
    remove,
  };
};
